/**
 * Horizontal carousel between sibling notes in the detail view.
 *
 * Same gesture standard as the rest of the product (`@/lib/gestures`):
 * recognizer with deadzone + X-axis lock, fling, haptic feedback on commit, and
 * respect for `prefers-reduced-motion` (no animation; direct navigation).
 *
 * Analogous to the listto drawer carousel (`todo-detail-route-view`), but
 * Apunte's detail remounts by route (`<Show keyed>` over pageId), so instead
 * of a multi-panel track we animate ONLY the EXIT of the current panel and then
 * navigate; the new page enters with its own render. Commit by ratio (style
 * `DRAWER_CAROUSEL`): confirms at whichever is smaller, a fixed distance or a
 * fraction of the width.
 *
 * It controls ONLY the horizontal axis: vertical drags yield to editor
 * scrolling. TipTap's contenteditable has `data-no-swipe` so it does not steal
 * text selection; we also abort if the gesture starts in an editable.
 */
import { createSignal } from "solid-js";

import { closestSelector, isEditableTarget } from "@/lib/gestures/guards";
import { prefersReducedMotion } from "@/lib/gestures/motion";
import { applyRubberBand, createGestureRecognizer } from "@/lib/gestures/recognizer";
import { vibrate } from "@/lib/gestures/haptics";
import { pointerHandlers } from "@/lib/gestures/solid";
import { NOTE_CAROUSEL } from "@/lib/gestures/presets";

const {
  thresholdPx: THRESHOLD_PX,
  commitThresholdPx: COMMIT_THRESHOLD_PX,
  commitThresholdRatio: COMMIT_RATIO,
  rubberBandFactor: RUBBER_BAND_FACTOR,
  transitionMs: TRANSITION_MS,
  fallbackWidthPx: FALLBACK_WIDTH_PX,
  hapticMs: HAPTIC_MS,
  fling: FLING,
} = NOTE_CAROUSEL;

interface NoteCarouselOptions {
  /** ID of the previous page in the flat list, or null if it is the first. */
  getPrevId: () => string | null;
  /** ID of the next page in the flat list, or null if it is the last. */
  getNextId: () => string | null;
  /** Navigates to a sibling page (the route remounts the detail). */
  navigate: (pageId: string) => void;
  /** Surface used to measure width for ratio-based commits. */
  getSurfaceRef: () => HTMLElement | undefined;
}

export function useNoteCarousel(options: NoteCarouselOptions) {
  const [dragX, setDragX] = createSignal(0);
  const [isAnimating, setIsAnimating] = createSignal(false);

  const width = () => options.getSurfaceRef()?.clientWidth ?? FALLBACK_WIDTH_PX;

  /** Destination for a direction; null if there is no sibling that way. */
  const targetFor = (direction: "next" | "prev"): string | null =>
    direction === "next" ? options.getNextId() : options.getPrevId();

  /** Animates the current panel out and navigates; skips animation for reduced motion. */
  const commit = (direction: "next" | "prev", targetId: string) => {
    if (prefersReducedMotion()) {
      setDragX(0);
      options.navigate(targetId);
      return;
    }
    const outX = direction === "next" ? -width() : width();
    setIsAnimating(true);
    setDragX(outX);
    vibrate(HAPTIC_MS);
    setTimeout(() => {
      options.navigate(targetId);
      // The detail remounts keyed by pageId, but the hook persists briefly
      // (Solid reconciles the wrapper). Reset without a transition so the
      // new content does not inherit the offset.
      setIsAnimating(false);
      setDragX(0);
    }, TRANSITION_MS);
  };

  const snapBack = () => {
    if (prefersReducedMotion()) {
      setDragX(0);
      return;
    }
    setIsAnimating(true);
    setDragX(0);
    setTimeout(() => {
      setIsAnimating(false);
    }, TRANSITION_MS);
  };

  const recognizer = createGestureRecognizer({
    axis: "x",
    thresholdPx: THRESHOLD_PX,
    fling: { ...FLING },
    canStart: ({ target }) => {
      // Do not steal the editor's text selection or start on controls.
      if (isEditableTarget(target)) {
        return false;
      }
      return !closestSelector(target, 'button, a, input, [data-no-swipe="true"]');
    },
    onStart: () => {
      setIsAnimating(false);
    },
    onMove: (sample) => {
      setIsAnimating(false);
      // Resistance when there is no sibling in the drag direction (the feeling
      // of "end of list"): dx<0 goes to the next one, dx>0 to the previous one.
      const wantsNext = sample.dx < 0;
      const hasTarget = wantsNext ? options.getNextId() !== null : options.getPrevId() !== null;
      setDragX(hasTarget ? sample.dx : applyRubberBand(sample.dx, 0, RUBBER_BAND_FACTOR));
    },
    onEnd: (sample) => {
      const direction: "next" | "prev" = sample.dx < 0 ? "next" : "prev";
      const targetId = targetFor(direction);
      const commitPx = Math.min(COMMIT_THRESHOLD_PX, width() * COMMIT_RATIO);
      const committed = targetId !== null && (sample.isFling || Math.abs(sample.dx) >= commitPx);
      if (committed && targetId) {
        commit(direction, targetId);
      } else {
        snapBack();
      }
    },
    onCancel: snapBack,
  });

  const base = pointerHandlers(recognizer);

  return {
    dragX,
    isAnimating,
    transitionMs: TRANSITION_MS,
    handlePointerDown: base.onPointerDown,
    handlePointerMove: base.onPointerMove,
    handlePointerUp: base.onPointerUp,
    handlePointerCancel: base.onPointerCancel,
  };
}
